import { useCallback, useEffect, useRef, useState } from 'react';

import type { AutosaveStatus } from './useAutosave';

// useManualSave is the explicit-save counterpart to useAutosave. Use it
// when persistence should happen on a user click, not on every edit —
// for forms where intermediate state is invalid (validation errors fire
// while typing) or where the persistence side-effect is heavy (e.g. it
// writes a version row to a history log so each save shows up as a
// distinct entry — the website design tool's history would otherwise
// fill with one row per keystroke).
//
// Surface mirrors useAutosave's status field so AutosaveStatusBadge can
// render either source without branching. The `dirty` flag is the source
// of truth for "the user has unsaved changes" — disable the Save button
// when !dirty so the action doesn't fire no-op writes.
//
// Dirty detection uses JSON.stringify against the last successfully
// saved snapshot. That's deep-equality enough for the shapes this hook
// is used with (arrays of plain objects, structs of primitives); add a
// custom equality fn parameter if a caller needs Map/Set support beyond
// what callers already normalize at the call site.

export interface ManualSaveControl {
  status: AutosaveStatus;
  error: string | null;
  // dirty is true when the current value differs from the last value
  // that successfully reached the server (or the initial value, before
  // any save has happened).
  dirty: boolean;
  // commit fires the save with the current value. No-op when !dirty so
  // a stray double-click can't double-write.
  commit: () => Promise<void>;
}

function extractErrorDetail(e: unknown): string {
  if (e && typeof e === 'object' && 'data' in e) {
    const data = (e as { data?: unknown }).data;
    if (data && typeof data === 'object' && 'detail' in data) {
      const detail = (data as { detail?: unknown }).detail;
      if (typeof detail === 'string' && detail.length > 0) {
        return detail;
      }
    }
  }
  if (e instanceof Error && e.message) {
    return e.message;
  }
  return 'save_failed';
}

function stableStringify(v: unknown): string {
  try {
    return JSON.stringify(v);
  } catch {
    return '';
  }
}

export function useManualSave<T>(
  value: T,
  save: (value: T) => Promise<void>,
  initial: T,
): ManualSaveControl {
  const [status, setStatus] = useState<AutosaveStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  // savedSnapshotRef holds the JSON form of the last value that
  // successfully reached the server. Comparing JSON strings is cheaper
  // than walking the object tree on every render and dodges reference-
  // identity issues for arrays we splice or sets we rebuild.
  const savedSnapshotRef = useRef<string>(stableStringify(initial));

  // Re-sync the snapshot when `initial` changes from outside. The wizard
  // / design-mode editor reloads `design` after some saves happen from
  // outside a panel (e.g. the disabled-banner "enable design mode" button
  // bypasses ModePanel and flips design_mode_enabled directly). Without
  // this effect the snapshot stays pinned to the value at mount and the
  // panel reports a phantom dirty state after the parent reload.
  const initialJSON = stableStringify(initial);
  useEffect(() => {
    savedSnapshotRef.current = initialJSON;
  }, [initialJSON]);

  const currentJSON = stableStringify(value);
  const dirty = currentJSON !== savedSnapshotRef.current;

  const commit = useCallback(async () => {
    if (!dirty) return;
    setStatus('pending');
    setError(null);
    try {
      await save(value);
      savedSnapshotRef.current = stableStringify(value);
      setStatus('saved');
    } catch (e) {
      setStatus('failed');
      setError(extractErrorDetail(e));
    }
  }, [dirty, save, value]);

  return { status, error, dirty, commit };
}
